import { Trophy, Medal, Award, Star, Eye, Target, Heart, Lightbulb, HandHeart, Sparkles } from 'lucide-react';
import PageHero from '@/components/PageHero';
import { Reveal, SectionHeading } from '@/components/Reveal';
import { ACHIEVEMENTS, VALUES, PRINCIPAL, STATS, IMG } from '@/data/content';

const VALUE_ICONS = [Heart, Star, Lightbulb, HandHeart];
const ACH_ICONS = [Trophy, Medal, Award, Sparkles];

const About = () => (
  <>
    <PageHero eyebrow="About Us" title="Rooted in values. Growing with vision." description="The story, mission and people behind Ideal Public School, Shahapur — serving families of Khetasarai, Jaunpur for over two decades." />

    <section className="py-24 sm:py-32" data-testid="history-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-14 lg:grid-cols-12 items-center">
        <div className="lg:col-span-6">
          <SectionHeading number="01" eyebrow="Our Story" title="From a small classroom to the region's trusted school." />
          <Reveal delay={0.2}>
            <div className="mt-6 space-y-5 text-navy/70 leading-relaxed">
              <p>Ideal Public School began with a simple belief — that the children of Shahapur and Khetasarai deserve the same quality of education as any big city. What started as a handful of students and one determined team is today a full campus serving 1200+ learners from Nursery to Class X.</p>
              <p>Our iconic red building, the morning assembly, the sound of the school bell — for thousands of families, these are memories that shaped their lives. And we are just getting started.</p>
            </div>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 gap-6">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={0.1 + i * 0.07}>
                <div className="rounded-lg border border-navy/10 bg-white p-5">
                  <p className="font-display text-3xl font-bold text-crimson">{s.value}</p>
                  <p className="mt-1 text-xs font-semibold uppercase tracking-[0.15em] text-navy/50">{s.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <div className="lg:col-span-6">
          <Reveal>
            <div className="relative">
              <div className="absolute -right-4 -top-4 h-full w-full rounded-lg border-2 border-crimson" aria-hidden="true" />
              <img src="/images/campus-wide.jpg" alt="Ideal Public School building" className="relative rounded-lg w-full object-cover aspect-[16/9]" />
              <img src={`${IMG.students}&w=600&q=75&auto=format&fit=crop`} alt="Students" className="absolute -bottom-8 -left-4 sm:-left-8 w-44 sm:w-56 rounded-lg border-4 border-cream object-cover aspect-square shadow-xl shadow-navy/20" />
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="bg-cream-dark py-24 sm:py-32" data-testid="vision-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading number="02" eyebrow="Vision & Mission" title="What we stand for" />
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {[
            { Icon: Eye, title: 'Our Vision', text: 'To nurture confident, capable and kind-hearted citizens who bring pride to their families, their village and their nation.' },
            { Icon: Target, title: 'Our Mission', text: 'To provide affordable, value-based, modern education — strong academics, strong character and strong bodies — to every child of our region.' },
          ].map(({ Icon, title, text }, i) => (
            <Reveal key={title} delay={i * 0.1}>
              <div className="h-full rounded-lg border border-navy/10 bg-white p-8 transition-all hover:-translate-y-1 hover:border-gold">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-crimson text-white"><Icon className="h-6 w-6" /></span>
                <h3 className="mt-5 font-display text-2xl font-bold text-navy">{title}</h3>
                <p className="mt-3 text-navy/70 leading-relaxed">{text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-14">
          <Reveal><h3 className="text-xs font-bold uppercase tracking-[0.25em] text-crimson">Our Core Values</h3></Reveal>
          <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => {
              const Icon = VALUE_ICONS[i];
              return (
                <Reveal key={v.title} delay={i * 0.08}>
                  <div data-testid={`value-${i}`} className="h-full rounded-lg bg-navy p-6 text-cream transition-all hover:-translate-y-1">
                    <Icon className="h-6 w-6 text-gold" />
                    <h4 className="mt-4 font-display text-xl font-bold">{v.title}</h4>
                    <p className="mt-2 text-sm text-cream/70 leading-relaxed">{v.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>

    <section className="py-24 sm:py-32" data-testid="principal-message-full">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 grid gap-14 lg:grid-cols-12 items-start">
        <div className="lg:col-span-4">
          <Reveal>
            <div className="relative max-w-xs">
              <div className="absolute -left-4 -top-4 h-full w-full rounded-lg border-2 border-gold" aria-hidden="true" />
              <img src={PRINCIPAL.photo} alt="Principal" className="relative rounded-lg w-full object-cover aspect-[4/5]" />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-6">
              <p className="font-display text-2xl font-bold text-navy">{PRINCIPAL.name}</p>
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-navy/50 mt-1">{PRINCIPAL.designation}</p>
              <p className="text-sm text-navy/60 mt-1">{PRINCIPAL.qualification}</p>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-8">
          <SectionHeading number="03" eyebrow="Principal's Message" title="A word from our Principal" />
          <div className="mt-6 space-y-5">
            {PRINCIPAL.message.map((p, i) => (
              <Reveal key={i} delay={0.15 + i * 0.08}>
                <p className={`leading-relaxed ${i === 0 ? 'font-display text-xl italic text-crimson' : 'text-navy/70'}`}>{p}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="bg-navy py-24 sm:py-32 noise-overlay" data-testid="achievements-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading dark number="04" eyebrow="Achievements & Awards" title="Results that speak for us" />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {ACHIEVEMENTS.map((a, i) => {
            const Icon = ACH_ICONS[i];
            return (
              <Reveal key={a.title} delay={i * 0.08}>
                <div data-testid={`achievement-${i}`} className="h-full rounded-lg border border-cream/10 bg-navy-light p-7 transition-all hover:-translate-y-1 hover:border-gold/50">
                  <Icon className="h-8 w-8 text-gold" />
                  <h3 className="mt-5 font-display text-xl font-bold text-cream">{a.title}</h3>
                  <p className="mt-3 text-sm text-cream/60 leading-relaxed">{a.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  </>
);

export default About;
